/**
 * ShareSettingsPage — "Enlace público" / "Public link" (PMA-ADMIN-014).
 *
 * Route: /admin/share-settings (admin-only via ProtectedRoute in App.tsx)
 *
 * Configures the per-section visibility (visible/blurred/hidden) of the
 * public share page (PMA-PUB-014) plus the shared upsell copy — one blur-card
 * message and one notice-bar message, each EN/ES, plus an optional contact
 * URL. There is ONE global upsell object, not one per section (team_plan.md,
 * locked API contract) — every blurred/hidden section shows the same
 * message, only the notice-bar visibility differs by whether ANY section is
 * restricted.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getShareSettings,
  putShareSettings,
  type ShareSettings,
} from "../../lib/shareSettingsApi";
import {
  DEFAULT_SHARE_VISIBILITY,
  SHARE_SECTION_KEYS,
  type ShareSectionKey,
  type ShareVisibilityState,
} from "../../lib/shareVisibility";
import LoadingSpinner from "../../components/LoadingSpinner";
import { Lock } from "../../components/icons";

const STATES: ShareVisibilityState[] = ["visible", "blurred", "hidden"];

const STATE_LABEL_KEY: Record<ShareVisibilityState, string> = {
  visible: "shareSettings.stateVisible",
  blurred: "shareSettings.stateBlurred",
  hidden: "shareSettings.stateHidden",
};

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  const data = (err as { response?: { data?: { detail?: string } } })?.response
    ?.data?.detail;
  if (typeof data === "string") return data;
  return "An unexpected error occurred";
}

function cloneDefaults(): ShareSettings {
  return {
    sections: { ...DEFAULT_SHARE_VISIBILITY.sections },
    upsell: { ...DEFAULT_SHARE_VISIBILITY.upsell },
  };
}

export default function ShareSettingsPage() {
  const { t } = useTranslation("admin");

  // ── Load state ────────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // ── Form state ────────────────────────────────────────────────────────────
  const [settings, setSettings] = useState<ShareSettings>(cloneDefaults());

  // ── Save state ────────────────────────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await getShareSettings();
        if (!cancelled) setSettings(s);
      } catch (err) {
        if (!cancelled) setLoadError(getErrorMessage(err));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  const setSectionState = (
    key: ShareSectionKey,
    state: ShareVisibilityState,
  ) => {
    setSettings((prev) => ({
      ...prev,
      sections: { ...prev.sections, [key]: state },
    }));
  };

  const setUpsellField = (
    field: "message_es" | "message_en" | "banner_es" | "banner_en" | "url",
    value: string,
  ) => {
    setSettings((prev) => ({
      ...prev,
      upsell: { ...prev.upsell, [field]: value },
    }));
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const trimmedUrl = settings.upsell.url?.trim();
      const updated = await putShareSettings({
        sections: settings.sections,
        upsell: {
          ...settings.upsell,
          url: trimmedUrl ? trimmedUrl : null,
        },
      });
      setSettings(updated);
      setSaveSuccess(true);
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  // Resets the FORM only — nothing is sent to the server until the admin
  // clicks Save, so an accidental reset never touches the live document.
  const handleReset = () => {
    if (window.confirm(t("shareSettings.resetConfirm"))) {
      setSettings(cloneDefaults());
      setSaveError(null);
      setSaveSuccess(false);
    }
  };

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center min-h-screen bg-gray-50"
        role="status"
        aria-live="polite"
        data-testid="share-settings-loading"
      >
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        className="flex items-center justify-center min-h-screen bg-gray-50"
        data-testid="share-settings-error"
      >
        <div className="text-sm text-gray-500">
          {t("shareSettings.loadError")}: {loadError}
        </div>
      </div>
    );
  }

  return (
    <div
      className="container mx-auto px-4 py-8 max-w-4xl"
      data-testid="share-settings-view"
    >
      {/* Page header */}
      <div className="mb-6">
        <h1
          className="text-3xl font-bold text-gray-900"
          data-testid="share-settings-heading"
        >
          {t("shareSettings.title")}
        </h1>
        <p className="text-gray-600 mt-2">{t("shareSettings.subtitle")}</p>
      </div>

      <div className="space-y-6">
        {/* Sections table */}
        <section
          className="rounded-xl border border-gray-200 bg-white p-6"
          data-testid="share-settings-sections"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t("shareSettings.sectionsTitle")}
          </h2>
          <div className="space-y-3">
            {SHARE_SECTION_KEYS.map((key) => (
              <div
                key={key}
                className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-3 last:border-0 last:pb-0"
                data-testid={`share-settings-row-${key}`}
              >
                <div>
                  <div className="text-sm font-semibold text-gray-800">
                    {t(`shareSettings.sections.${key}.name`)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {t(`shareSettings.sections.${key}.description`)}
                  </div>
                </div>
                <div
                  className="flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1"
                  role="group"
                >
                  {STATES.map((state) => (
                    <button
                      key={state}
                      type="button"
                      data-testid={`share-visibility-${key}-${state}`}
                      aria-pressed={settings.sections[key] === state}
                      onClick={() => setSectionState(key, state)}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                        settings.sections[key] === state
                          ? "bg-primary-600 text-white"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      {t(STATE_LABEL_KEY[state])}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Messages */}
        <section
          className="rounded-xl border border-gray-200 bg-white p-6"
          data-testid="share-settings-messages"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-1">
            {t("shareSettings.messagesTitle")}
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            {t("shareSettings.blurDisclaimer")}
          </p>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                {t("shareSettings.blurCardMessageTitle")}
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="share-settings-message-es"
                    className="block text-xs font-medium text-gray-600 mb-1"
                  >
                    {t("shareSettings.messageEs")}
                  </label>
                  <textarea
                    id="share-settings-message-es"
                    data-testid="share-settings-message-es"
                    value={settings.upsell.message_es}
                    onChange={(e) =>
                      setUpsellField("message_es", e.target.value)
                    }
                    rows={2}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="share-settings-message-en"
                    className="block text-xs font-medium text-gray-600 mb-1"
                  >
                    {t("shareSettings.messageEn")}
                  </label>
                  <textarea
                    id="share-settings-message-en"
                    data-testid="share-settings-message-en"
                    value={settings.upsell.message_en}
                    onChange={(e) =>
                      setUpsellField("message_en", e.target.value)
                    }
                    rows={2}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                {t("shareSettings.noticeBarMessageTitle")}
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="share-settings-banner-es"
                    className="block text-xs font-medium text-gray-600 mb-1"
                  >
                    {t("shareSettings.bannerEs")}
                  </label>
                  <textarea
                    id="share-settings-banner-es"
                    data-testid="share-settings-banner-es"
                    value={settings.upsell.banner_es}
                    onChange={(e) =>
                      setUpsellField("banner_es", e.target.value)
                    }
                    rows={2}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="share-settings-banner-en"
                    className="block text-xs font-medium text-gray-600 mb-1"
                  >
                    {t("shareSettings.bannerEn")}
                  </label>
                  <textarea
                    id="share-settings-banner-en"
                    data-testid="share-settings-banner-en"
                    value={settings.upsell.banner_en}
                    onChange={(e) =>
                      setUpsellField("banner_en", e.target.value)
                    }
                    rows={2}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>

            <div>
              <label
                htmlFor="share-settings-url"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                {t("shareSettings.contactUrlLabel")}
              </label>
              <input
                id="share-settings-url"
                type="text"
                data-testid="share-settings-url"
                value={settings.upsell.url ?? ""}
                onChange={(e) => setUpsellField("url", e.target.value)}
                placeholder={t("shareSettings.contactUrlPlaceholder")}
                className="w-full max-w-md rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
        </section>

        {/* Previews */}
        <section
          className="rounded-xl border border-gray-200 bg-white p-6"
          data-testid="share-settings-previews"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                {t("shareSettings.previewBlurTitle")}
              </h3>
              <div
                className="relative overflow-hidden rounded-xl border border-surface-dark-border bg-surface-dark-page p-6"
                data-testid="share-settings-preview-blur"
              >
                <div
                  className="rounded-lg bg-slate-700/40 p-6 text-center"
                  style={{ filter: "blur(2px)" }}
                  aria-hidden="true"
                >
                  <div className="h-16 rounded bg-slate-600/40" />
                </div>
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-4">
                  <div className="max-w-xs rounded-xl border border-surface-dark-border bg-surface-dark-card/95 p-3 text-center shadow-xl">
                    <Lock
                      className="mx-auto mb-1 h-5 w-5 text-slate-300"
                      aria-hidden="true"
                    />
                    <p className="text-xs text-slate-200">
                      {settings.upsell.message_en}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                {t("shareSettings.previewBannerTitle")}
              </h3>
              <div
                className="flex flex-wrap items-center justify-center gap-2 rounded-xl border border-surface-dark-border bg-surface-dark-card px-4 py-3 text-center text-sm text-slate-200"
                data-testid="share-settings-preview-banner"
              >
                <Lock className="h-4 w-4 text-slate-300" aria-hidden="true" />
                <span>{settings.upsell.banner_en}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Save / Reset */}
        <div className="flex items-center gap-4" data-testid="save-row">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            data-testid="share-settings-save"
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? t("shareSettings.saving") : t("shareSettings.save")}
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={isSaving}
            data-testid="share-settings-reset"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("shareSettings.reset")}
          </button>

          {saveSuccess && (
            <p
              className="text-sm text-green-700"
              data-testid="share-settings-save-success"
            >
              {t("shareSettings.saveSuccess")}
            </p>
          )}
        </div>

        {saveError && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            data-testid="share-settings-save-error"
          >
            {saveError}
          </div>
        )}
      </div>
    </div>
  );
}
